import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { type AxiosError, type AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { MUTATION_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { AuthService } from 'services/auth';
import { type SignupParams, type SignupResponse } from 'services/auth/types';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { clearReferralParamsFromSession } from 'utils/referral';

export const useRegisterUser = ({
  onError,
}: {
  onError: () => void;
}): UseMutationResult<AxiosResponse<SignupResponse>, AxiosError, SignupParams> => {
  const navigate = useNavigate();
  const { trackSignUpCompleted } = useTrackingEvents();
  const { t } = useTranslation(LOCIZE_NS.AUTH);
  return useMutation<AxiosResponse<SignupResponse>, AxiosError, SignupParams>({
    mutationKey: [MUTATION_KEYS.REGISTER_USER],
    mutationFn: async (body: SignupParams) => await AuthService.signup(body),
    onSuccess: response => {
      toast.success(t('SignupForm.success'));
      clearReferralParamsFromSession();
      trackSignUpCompleted(response.data);
      navigate(ROUTES.ROOT);
    },
    onError: () => {
      onError();
      toast.error(t('SignupForm.create.error'));
    },
  });
};
